/**
 * Billing hooks using TanStack Query
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { billingService } from "@/services/billingService";
import type { BillingStatus, Plan, Invoice, UpcomingInvoice, ProrationPreview } from "@/types/billing";

/**
 * Hook to fetch billing status
 */
export function useBillingStatus() {
  return useQuery<BillingStatus>({
    queryKey: ["billing", "status"],
    queryFn: () => billingService.getStatus(),
    staleTime: 0,
    refetchOnMount: "always",
  });
}

/**
 * Hook to fetch available plans
 */
export function usePlans() {
  return useQuery<Plan[]>({
    queryKey: ["billing", "plans"],
    queryFn: () => billingService.getPlans(),
    staleTime: 0,
    refetchOnMount: "always",
  });
}

/**
 * Hook to fetch invoices
 */
export function useInvoices() {
  return useQuery<Invoice[]>({
    queryKey: ["billing", "invoices"],
    queryFn: () => billingService.getInvoices(),
    staleTime: 0,
    refetchOnMount: "always",
  });
}

/**
 * Hook to fetch upcoming invoice
 */
export function useUpcomingInvoice() {
  return useQuery<UpcomingInvoice | null>({
    queryKey: ["billing", "upcoming-invoice"],
    queryFn: () => billingService.getUpcomingInvoice(),
    staleTime: 0,
    refetchOnMount: "always",
  });
}

/**
 * Hook to create checkout session (redirect to Stripe)
 */
export function useCreateCheckout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tierName,
      successUrl,
      cancelUrl,
    }: {
      tierName: string;
      successUrl: string;
      cancelUrl: string;
    }) => {
      const result = await billingService.createCheckout(tierName, successUrl, cancelUrl);
      // Redirect to Stripe Checkout
      window.location.href = result.checkout_url;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["billing"] });
    },
  });
}

/**
 * Hook to change tier on existing subscription.
 * After the change succeeds, waits briefly for the webhook to process then reloads.
 */
export function useChangeTier() {
  const [isReloading, setIsReloading] = useState(false);

  const mutation = useMutation({
    mutationFn: (tierName: string) => billingService.changeTier(tierName),
    onSuccess: () => {
      setIsReloading(true);
      // Brief delay to allow the webhook to process before reloading
      setTimeout(() => window.location.reload(), 3000);
    },
  });

  return { ...mutation, isPolling: isReloading };
}

/**
 * Hook to cancel subscription
 */
export function useCancelSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => billingService.cancelSubscription(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["billing"] });
      queryClient.invalidateQueries({ queryKey: ["organization"] });
    },
  });
}

/**
 * Hook to reactivate subscription
 */
export function useReactivateSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => billingService.reactivateSubscription(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["billing"] });
      queryClient.invalidateQueries({ queryKey: ["organization"] });
    },
  });
}

/**
 * Hook to preview proration before switching tiers
 */
export function usePreviewTierChange() {
  return useMutation<ProrationPreview, Error, string>({
    mutationFn: (tierName: string) => billingService.previewTierChange(tierName),
  });
}

/**
 * Hook to get Customer Portal URL
 */
export function usePortalSession() {
  return useMutation({
    mutationFn: async () => {
      const result = await billingService.getPortalSession();
      // Redirect to Stripe Portal
      window.location.href = result.portal_url;
      return result;
    },
  });
}
